import { MatchDayType, RoundMode } from '@uefadigital/panenka';
import dayjs from 'dayjs';

import type { MatchesCalendarTab } from '../store/types';
import { MatchesCalendarTabType } from '../store/types';

import { customDateFormat } from './mapping';

import { hydrateWithTranslations } from '@services/TranslatorService';
import type { useTranslateFn } from '@shared/Translate';
import type { Match } from '@shared-types/match.types';
import { uniqBy } from '@utilities/collection-utils';
import { dateToString } from '@utilities/dateTime';

export const getTabPrimaryText = ({ type, date, round, matchdays, draw }: MatchesCalendarTab) => {
  switch (type) {
    case MatchesCalendarTabType.DATE:
      return dateToString(date, 'ddd D MMM');
    case MatchesCalendarTabType.ROUND:
      return hydrateWithTranslations(round).name;
    case MatchesCalendarTabType.MATCHDAY: {
      const matchDayName = hydrateWithTranslations(matchdays.at(0)).longName;
      const roundName = hydrateWithTranslations(round).name;
      return `${roundName} - ${matchDayName}`;
    }
    case MatchesCalendarTabType.DRAW:
      return hydrateWithTranslations(draw)?.title;
  }
};

export const getTabSecondaryText = ({ type, date, matchdays, round }: MatchesCalendarTab) => {
  switch (type) {
    case MatchesCalendarTabType.DATE:
      return null;
    case MatchesCalendarTabType.ROUND:
    case MatchesCalendarTabType.MATCHDAY: {
      const dateStringFrom = matchdays?.at(0)?.dateFrom ?? round.dateFrom;
      const dateFrom = dateStringFrom ? dayjs(dateStringFrom, customDateFormat).add(12, 'h') : null;

      const dateToFrom = matchdays?.at(-1)?.dateTo ?? round.dateTo;
      const dateTo = dateToFrom ? dayjs(dateToFrom, customDateFormat).add(-12, 'h') : null;

      if (!dateFrom || !dateTo) return (dateFrom ?? dateTo)?.format('MMM YYYY');

      if (dateFrom.isSame(dateTo, 'month')) return dateFrom.format('MMM YYYY');

      if (dateFrom.isSame(dateTo, 'year')) return `${dateFrom.format('MMM')} - ${dateTo.format('MMM YYYY')}`;

      return `${dateFrom.format('MMM YYYY')} - ${dateTo.format('MMM YYYY')}`;
    }
    case MatchesCalendarTabType.DRAW:
      return dayjs(date).format('DD MMM YYYY');
    default: {
      if (!date) return;
      return dayjs(date).add(12, 'h').format('MMM YYYY');
    }
  }
};

export const getTabLegLabel = ({ type, round, matchdays }: MatchesCalendarTab) => {
  if (
    type === MatchesCalendarTabType.ROUND &&
    round?.mode === RoundMode.KNOCK_OUT &&
    round.modeDetail === 'KNOCK_OUT_TWO_LEGS'
  ) {
    return matchdays?.at(0)?.type === MatchDayType.FIRST_LEG ? '1stleg_abbr' : '2ndleg_abbr';
  }

  return null;
};

export const getMatchdayName = (matches: Match[]) => {
  const matchdays = uniqBy(matches.map((m) => m.matchday).filter(Boolean), 'id');
  return matchdays.length > 1
    ? matchdays
        .map((md) => hydrateWithTranslations(md).name)
        .filter(Boolean)
        .toSorted((a, b) => a.localeCompare(b))
        .join(' - ')
    : hydrateWithTranslations(matchdays.at(0))?.longName;
};

export const getRoundLabel = (matches: Match[], t: ReturnType<typeof useTranslateFn>, hideMatchday?: boolean) => {
  const { round, matchday } = matches[0] ?? ({} as Match);
  if (round?.mode === RoundMode.GROUP) {
    const matchDayName = hideMatchday ? '' : `- ${getMatchdayName(matches)}`;
    return `${hydrateWithTranslations(round)?.name} ${matchDayName}`;
  }
  const legLabel = t(matchday?.type === MatchDayType.FIRST_LEG ? '1stleg_abbr' : '2ndleg_abbr');

  if (round?.mode === RoundMode.KNOCK_OUT && round?.modeDetail === 'KNOCK_OUT_TWO_LEGS') {
    return `${hydrateWithTranslations(round)?.name} - ${legLabel}`;
  }

  return hydrateWithTranslations(round)?.name;
};
